import { Component, h, Prop, Watch, State } from '@stencil/core';
import { ChartData, ChartTooltipItem } from 'chart.js';
import { EfficiencyModel, EfficiencyDisplay } from '../types';
import { GRAPH_COLOR_MAIN } from '../../lib/graph/constants';
import { getDataPoints, getStepValues } from '../../../utils/utils';
import { Fragment } from '../../functional/Fragment';
import { round } from '../../../utils/math';
import { getFormatter } from '../../../utils/formatting';
import { Maybe, LayoutSize } from '../../../global/types';
import { getLayoutClass } from '../../../global/constants';
import { efficiency } from '../../../utils/equation';
import { TableRow } from '../../lib/print/print';

@Component({
  tag: 'rt-efficiency',
  styleUrl: 'efficiency.css',
  shadow: true,
})
export class EfficiencyComponent {
  @Prop() model: EfficiencyModel;

  @Prop() display: EfficiencyDisplay = 'default';

  @Prop() locale;

  @State() layout: Maybe<LayoutSize>;

  private loadFormatter;
  private efficiencyFormatter;
  private graphEl: HTMLRtGraphElement;

  @Watch('locale')
  changeFormatter() {
    this.loadFormatter = getFormatter(this.locale);
    this.efficiencyFormatter = getFormatter(this.locale, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
  }

  connectedCallback() {
    this.changeFormatter();
  }

  private formatLoad(power: number, withScale = true): string {
    return `${this.loadFormatter.format(round(power, 0))}${
      withScale ? ' %' : ''
    }`;
  }

  private formatEfficiency(efficiency: number, withScale = true): string {
    return `${this.efficiencyFormatter.format(round(efficiency, 1))}${
      withScale ? ' %' : ''
    }`;
  }

  renderGraph() {
    const labels = getDataPoints(
      this.model.curveLoadMin,
      this.model.curveLoadMax,
      Math.floor(this.model.curveLoadMax - this.model.curveLoadMin),
    );
    const efficiencyFunction = efficiency(this.model);

    const data: ChartData = {
      datasets: [
        {
          label: `${this.model.id}`,
          borderColor: GRAPH_COLOR_MAIN,
          backgroundColor: GRAPH_COLOR_MAIN,
          borderWidth: 2,
          data: labels.map((x) => ({ x, y: efficiencyFunction(x) })),
          showLine: true,
          fill: false,
        },
      ],
    };
    return (
      <rt-graph
        ref={(el) => (this.graphEl = el)}
        type="scatter"
        size={{ height: '350px' }}
        data={data}
        options={{
          scales: {
            xAxes: [
              {
                scaleLabel: {
                  labelString: `${this.model.translations.xAxisLabel} (%)`,
                },
                ticks: {
                  callback: (value: string) => {
                    return `${+value}`;
                  },
                },
                afterBuildTicks: (_, ticks) => ticks,
              },
            ],
            yAxes: [
              {
                type: 'linear',
                ticks: {
                  min: this.model.axis.y.min ?? 0,
                  max: this.model.axis.y.max ?? 100,
                },
                scaleLabel: {
                  labelString: `${this.model.translations.yAxisLabel} (%)`,
                },
              },
            ],
          },

          tooltips: {
            titleMarginBottom: 2,
            titleFontStyle: 'normal',
            callbacks: {
              title: ([tooltipItem]) => {
                const load = tooltipItem !== undefined
                  ? this.formatLoad(+tooltipItem.xLabel)
                  : '';
                return [
                  `${this.model.translations.xAxisLabel}:`,
                  load,
                  this.model.id,
                ];
              },
              label: (tooltipItem: ChartTooltipItem) => {
                const efficiency = this.formatEfficiency(+tooltipItem.yLabel);
                return [`${this.model.translations.yAxisLabel}:`, efficiency];
              },
            },
            displayColors: false,
          },
          legend: {
            display: false,
          },

          elements: {
            line: {
              tension: 0.4,
            },
          },
        }}
      ></rt-graph>
    );
  }

  getTableLabels() {
    return getStepValues(this.model.curveLoadMin, this.model.curveLoadMax, 25);
  }

  renderTable() {
    const tableLabels = this.getTableLabels();

    return (
      <div class="efficiency__table">
        <rt-table
          columns={tableLabels.map((load) => this.formatLoad(load))}
          rows={[
            {
              render: this.model.url ? (
                <se-link url={this.model.url}>{this.model.id}</se-link>
              ) : (
                <span class="row-1">{this.model.id}</span>
              ),
              labels: tableLabels.map((load) => ({
                label: this.formatLoad(load),
                value: this.formatEfficiency(efficiency(this.model)(load)),
              })),
            },
          ]}
          columnLabelTitle={this.model.translations.xAxisLabel}
          columnValueTitle={this.model.translations.yAxisLabel}
          class={getLayoutClass(this.layout)}
        ></rt-table>
      </div>
    );
  }

  printLink() {
    const { translations, name, id, family, url } = this.model;

    const tableData = () => {
      const labels = this.getTableLabels();
      return {
        title: `${translations.xAxisLabel} / ${translations.yAxisLabel}`,
        columns: labels.map((load) => this.formatLoad(load)),
        rows: [
          [
            [{ id, url }],
            ...labels.map((load) => this.formatEfficiency(efficiency(this.model)(load))),
          ],
        ] as TableRow[],
      };
    };

    return (
      <rt-print
        data={{
          linkText: translations.printLabel,
          type: translations.yAxisLabel,
          name,
          id,
          family,
          infoContent: translations.info,
          graphEl: () => this.graphEl,
          tableContent: this.display === 'graph' ? null : tableData(),
        }}
      ></rt-print>
    );
  }

  renderInfo() {
    const {
      translations: { info },
    } = this.model;
    if (info) {
      return (
        <rt-info class={getLayoutClass(this.layout)}>
          <span slot="info">{info}</span>
          {this.printLink()}
        </rt-info>
      );
    }
    return this.printLink();
  }

  renderAll() {
    switch (this.display) {
      case 'graph': {
        return (
          <Fragment>
            {this.renderGraph()}
            {this.renderInfo()}
          </Fragment>
        );
      }
      case 'table': {
        return (
          <Fragment>
            {this.renderTable()}
            {this.printLink()}
          </Fragment>
        );
      }
      default: {
        return (
          <Fragment>
            {this.renderGraph()}
            {this.renderTable()}
            {this.renderInfo()}
          </Fragment>
        );
      }
    }
  }

  handleLayoutChange = (e: CustomEvent) => {
    this.layout = e.detail;
  };

  render() {
    return (
      <rt-resize-watcher onBreakPointChanged={this.handleLayoutChange}>
        {this.renderAll()}
      </rt-resize-watcher>
    );
  }
}
